import { Controller } from "@hotwired/stimulus"

// ⋯ menu that portals its dropdown to document.body to escape overflow:hidden cards.
export default class extends Controller {
  static targets = ["items"]

  disconnect() {
    this._removePortal()
  }

  toggle(event) {
    event.stopPropagation()
    if (this._portal) {
      this._removePortal()
    } else {
      this._openPortal(event.currentTarget)
    }
  }

  _openPortal(button) {
    const rect = button.getBoundingClientRect()

    const menu = document.createElement("div")
    Object.assign(menu.style, {
      position:     "fixed",
      top:          `${rect.bottom + 4}px`,
      right:        `${window.innerWidth - rect.right}px`,
      background:   "#fff",
      border:       "1px solid #f3f4f6",
      borderRadius: "12px",
      boxShadow:    "0 8px 24px rgba(0,0,0,0.14)",
      // Explicit width, not shrink-to-fit: with only minWidth set, WKWebView
      // resolved the width to full-screen on some pages (e.g. the match page),
      // so the dropdown spanned edge-to-edge instead of sitting top-right.
      width:        "210px",
      maxWidth:     "calc(100vw - 24px)",
      zIndex:       "99997",
      overflow:     "hidden",
    })

    // <template data-card-menu-target="items"> holds the action definitions.
    // Must query .content (DocumentFragment) to reach the buttons inside.
    if (this.hasItemsTarget) {
      this.itemsTarget.content.querySelectorAll("button[data-action-label]").forEach(item => {
        const btn = document.createElement("button")
        Object.assign(btn.style, {
          width:      "100%",
          padding:    "13px 16px",
          background: "none",
          border:     "none",
          textAlign:  "left",
          fontFamily: "'DM Sans',sans-serif",
          fontSize:   "14px",
          fontWeight: "500",
          color:      item.dataset.actionColor || "#dc2626",
          cursor:     "pointer",
        })
        btn.textContent = item.dataset.actionLabel
        btn.addEventListener("click", (e) => {
          e.stopPropagation()
          this._removePortal()
          if (item.dataset.actionNavigate === "true") {
            window.location.href = item.dataset.actionUrl
          } else {
            this._runAction(
              item.dataset.actionUrl,
              item.dataset.actionMethod || "POST",
              item.dataset.actionReload === "true"
            )
          }
        })
        menu.appendChild(btn)
      })
    }

    document.body.appendChild(menu)
    this._portal = menu

    this._outside = (e) => {
      if (!menu.contains(e.target) && !this.element.contains(e.target)) {
        this._removePortal()
      }
    }
    requestAnimationFrame(() => document.addEventListener("click", this._outside))
  }

  _removePortal() {
    this._portal?.remove()
    this._portal = null
    document.removeEventListener("click", this._outside)
  }

  async _runAction(url, method, reload = false) {
    let resp
    try {
      resp = await fetch(url, {
        method,
        headers: {
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content,
          "Accept":       "application/json",
        }
      })
    } catch (err) {
      console.error("[card-menu] fetch failed:", err)
      return
    }

    if (!resp.ok) {
      console.error("[card-menu] action failed:", resp.status, url)
      return
    }

    if (reload) {
      // Keep the card but refresh the feed (e.g. after leaving an event)
      window.location.href = "/discoveries"
    } else {
      // Animate the card out and remove it from the DOM. The controller lives on
      // (or inside) the .disc-card, so close on the card itself — NOT its parent,
      // which is the whole #feed-list container.
      const card = this.element.closest("turbo-frame") || this.element.closest(".disc-card")
      if (card) {
        Object.assign(card.style, {
          transition: "opacity 0.25s ease, transform 0.25s ease",
          opacity:    "0",
          transform:  "scale(0.97)",
        })
        setTimeout(() => {
          card.remove()
          // If that was the last card in the feed, reload so the server can
          // render the empty state (which lives in a branch that only shows
          // when the feed is empty — a bare #feed-list would otherwise be blank).
          const feed = document.getElementById("feed-list")
          if (feed && !feed.querySelector(".disc-card")) {
            window.location.href = "/discoveries"
          }
        }, 260)
      }
    }
  }
}
